import type { NodeConnections, ProductRoutingRule, RouteWeight } from '../../types/nodeConfig';
import { formatRouteLabel } from '../../lib/routeLabels';
import SearchableRouteSelect from './SearchableRouteSelect';

interface Props {
  node: Record<string, unknown>;
  connections: NodeConnections;
  productTypeOptions: Array<{ id: string; name: string }>;
  readOnly?: boolean;
  onChange: (patch: Record<string, unknown>) => void;
}

export default function SplitterNodeConfig({
  node,
  connections,
  productTypeOptions,
  readOnly,
  onChange,
}: Props) {
  const mode = String(node.distribution_type ?? 'equal');
  const routeWeights = (node.route_weights as RouteWeight[] | undefined) ?? [];
  const rules = (node.routing_rules as ProductRoutingRule[] | undefined) ?? [];
  const outgoing = connections.outgoing;

  const ensureRouteWeights = () => {
    if (routeWeights.length === outgoing.length) return routeWeights;
    return outgoing.map((r, i) => ({
      route_id: r.route_id,
      percentage: routeWeights[i]?.percentage ?? Math.round(100 / Math.max(outgoing.length, 1)),
    }));
  };

  const weights = ensureRouteWeights();
  const weightTotal = weights.reduce((s, w) => s + w.percentage, 0);
  const weightValid = Math.abs(weightTotal - 100) < 0.01;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Splitter configuration</h3>

      <div>
        <label className="mb-1 block text-xs text-text-muted">Distribution mode</label>
        <select
          className="input-field w-full max-w-sm"
          disabled={readOnly}
          value={mode}
          onChange={(e) => onChange({ distribution_type: e.target.value })}
        >
          <option value="equal">Equal split</option>
          <option value="weighted">Weighted split</option>
          <option value="round_robin">Round robin</option>
          <option value="by_product_type">By product type</option>
        </select>
      </div>

      {mode === 'equal' && (
        <p className="text-sm text-text-muted">
          Products will be split equally across {outgoing.length} outgoing route
          {outgoing.length !== 1 ? 's' : ''}.
        </p>
      )}

      {mode === 'round_robin' && (
        <p className="text-sm text-text-muted">
          Products are sent in sequence across outgoing routes.
        </p>
      )}

      {mode === 'weighted' && (
        <div className="space-y-2">
          {weights.map((rw, index) => {
            const route = outgoing.find((o) => o.route_id === rw.route_id);
            return (
              <div key={rw.route_id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm text-text">
                  {route ? formatRouteLabel(route, 'outgoing') : rw.route_id}
                </span>
                <input
                  type="number"
                  min={0}
                  className="input-field w-20"
                  disabled={readOnly}
                  value={rw.percentage}
                  onChange={(e) => {
                    const next = ensureRouteWeights();
                    next[index] = {
                      ...rw,
                      percentage: Number(e.target.value),
                    };
                    onChange({ route_weights: next });
                  }}
                />
                <span className="text-xs text-text-subtle">%</span>
              </div>
            );
          })}
          <p
            className={`text-xs ${weightValid ? 'text-text-subtle' : 'text-danger'}`}
          >
            Total: {weightTotal.toFixed(1)}%
            {!weightValid && ' — must sum to 100%'}
          </p>
        </div>
      )}

      {mode === 'by_product_type' && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-text-muted">Fallback route</label>
            <SearchableRouteSelect
              routes={outgoing}
              value={String(node.fallback_route_id ?? '')}
              direction="outgoing"
              disabled={readOnly}
              placeholder="None"
              onChange={(routeId) =>
                onChange({
                  fallback_route_id: routeId || undefined,
                })
              }
            />
          </div>

          {!readOnly && (
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() =>
                onChange({
                  routing_rules: [
                    ...rules,
                    {
                      product_type_id: productTypeOptions[0]?.id ?? '',
                      route_id: outgoing[0]?.route_id ?? '',
                      percentage: 100,
                      priority: 0,
                      enabled: true,
                    },
                  ],
                })
              }
            >
              Add routing rule
            </button>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-text-subtle">
                <tr>
                  <th className="pb-2 pr-2">Product</th>
                  <th className="pb-2 pr-2">Route</th>
                  <th className="pb-2 pr-2">%</th>
                  <th className="pb-2 pr-2">On</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule, index) => (
                  <tr key={index} className="border-t border-border/60">
                    <td className="py-2 pr-2">
                      <select
                        className="input-field py-1"
                        disabled={readOnly}
                        value={rule.product_type_id}
                        onChange={(e) => {
                          const next = [...rules];
                          next[index] = {
                            ...rule,
                            product_type_id: e.target.value,
                          };
                          onChange({ routing_rules: next });
                        }}
                      >
                        {productTypeOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <SearchableRouteSelect
                        routes={outgoing}
                        value={rule.route_id}
                        direction="outgoing"
                        disabled={readOnly}
                        onChange={(routeId) => {
                          const next = [...rules];
                          next[index] = { ...rule, route_id: routeId };
                          onChange({ routing_rules: next });
                        }}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <input
                        type="number"
                        className="input-field w-16 py-1"
                        disabled={readOnly}
                        value={rule.percentage}
                        onChange={(e) => {
                          const next = [...rules];
                          next[index] = {
                            ...rule,
                            percentage: Number(e.target.value),
                          };
                          onChange({ routing_rules: next });
                        }}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={rule.enabled !== false}
                        onChange={(e) => {
                          const next = [...rules];
                          next[index] = { ...rule, enabled: e.target.checked };
                          onChange({ routing_rules: next });
                        }}
                      />
                    </td>
                    <td className="py-2">
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn-danger-ghost"
                          onClick={() =>
                            onChange({
                              routing_rules: rules.filter((_, i) => i !== index),
                            })
                          }
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto rounded-md bg-surface p-3">
            <p className="mb-2 text-xs font-medium text-text">Rule preview</p>
            <table className="w-full text-left text-xs text-text-muted">
              <thead className="text-text-subtle">
                <tr>
                  <th className="pb-1 pr-2">If product</th>
                  <th className="pb-1">Route to</th>
                </tr>
              </thead>
              <tbody>
                {rules
                  .filter((r) => r.enabled !== false)
                  .map((rule, i) => {
                    const product = productTypeOptions.find(
                      (p) => p.id === rule.product_type_id,
                    );
                    const route = outgoing.find((r) => r.route_id === rule.route_id);
                    return (
                      <tr key={i} className="border-t border-border/40">
                        <td className="py-1 pr-2">{product?.name ?? '?'}</td>
                        <td className="py-1">
                          {route
                            ? formatRouteLabel(route, 'outgoing')
                            : '?'}
                          {' '}({rule.percentage}%)
                        </td>
                      </tr>
                    );
                  })}
                <tr className="border-t border-border/40">
                  <td className="py-1 pr-2 text-text-subtle">Otherwise</td>
                  <td className="py-1">
                    {outgoing.find((r) => r.route_id === node.fallback_route_id)
                      ? formatRouteLabel(
                          outgoing.find(
                            (r) => r.route_id === node.fallback_route_id,
                          )!,
                          'outgoing',
                        )
                      : 'no fallback'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
